import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { gaMeasurementId } from '../feature-flags';

// The visitor's answer to the cookie banner, kept for a year, then asked again.
const CONSENT_KEY = '3dway.cookie-consent.v1';
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export type AnalyticsConsent = 'granted' | 'denied';

interface StoredConsent {
  analytics: AnalyticsConsent;
  at: string;
}

interface GaPage {
  page_location: string;
  page_referrer: string;
  page_title: string;
}

// Google Analytics 4 behind the cookie banner (Alex, 2026-11-03: production only, tied to cookie
// consent, Enhanced Measurement off, no customer file names, model ids or private URLs).
//
// - Off everywhere but production (`gaMeasurementId`): `enabled` is false, nothing below runs.
// - Nothing of Google's loads before the visitor accepts: no script, no cookie, no request
//   (consent mode "basic"). Rejecting, or a browser's Global Privacy Control signal with no answer
//   given yet, keeps it that way. Withdrawing turns GA off for the page and deletes its cookies.
//   An answer given in another tab applies here too (the `storage` event).
// - Page views are sent by hand on every navigation — Enhanced Measurement is off, so GA sends none
//   itself. Each one carries the route's pattern, never the address: /render/preview/:modelId, no
//   query (Stripe's session id), no fragment, and the referrer cut to its origin. Titles are the
//   app's own (`3DWay`, the legal documents'): a page title must never carry a customer's data.
//   GA is loaded on the first page that may be counted, with that page's cleaned address set before
//   its `config`, so not even GA's own events (session start, engagement) see the browser's address.
// - /admin is the team's: GA is switched off there (`ga-disable-<id>`) and never loaded by it.
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  public readonly enabled = !!gaMeasurementId;
  // Whether the banner is up: on a first visit, a year after the answer, and from "Cookie settings".
  public readonly bannerOpen$ = new BehaviorSubject<boolean>(false);
  // The visitor's answer as it stands (null: none given yet, or the banner never started), for
  // ProductAnalyticsService, which follows the same choice.
  public readonly consent$ = new BehaviorSubject<AnalyticsConsent | null>(null);

  private consent: AnalyticsConsent | null = null;
  private started = false;
  private loaded = false;
  // What gtag was last told about analytics_storage, once loaded.
  private gtagGranted = false;
  private previousLocation: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public get currentConsent(): AnalyticsConsent | null {
    return this.consent;
  }

  // Called once by the banner, which the app root draws only when `enabled`.
  public start(): void {
    if (!this.enabled || this.started) {
      return;
    }
    this.started = true;
    this.consent = this.readConsent();
    this.consent$.next(this.consent);
    this.bannerOpen$.next(this.consent === null && !this.globalPrivacyControl());
    // A tick later: the new page sets its title in its own init, after NavigationEnd.
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.zone.runOutsideAngular(() => setTimeout(() => this.sendPageView()));
    });
    window.addEventListener('storage', (event) => {
      if (event.key === CONSENT_KEY || event.key === null) {
        this.zone.run(() => this.applyConsent(this.readConsent()));
      }
    });
    // The banner can come up after the first navigation has ended: count that page too.
    if (this.consent === 'granted' && this.router.navigated) {
      this.sendPageView();
    }
  }

  public openSettings(): void {
    if (this.enabled) {
      this.bannerOpen$.next(true);
    }
  }

  public closeSettings(): void {
    this.bannerOpen$.next(false);
  }

  public setConsent(consent: AnalyticsConsent): void {
    if (!this.enabled) {
      return;
    }
    this.writeConsent(consent);
    this.applyConsent(consent);
  }

  // A new answer, from the banner here or another tab. Accepting again what was accepted counts nothing.
  private applyConsent(consent: AnalyticsConsent | null): void {
    const previous = this.consent;
    this.consent = consent;
    this.consent$.next(consent);
    if (consent !== null) {
      this.bannerOpen$.next(false);
    }
    if (consent === 'granted') {
      if (previous !== 'granted') {
        this.sendPageView();
      }
    } else {
      this.disable();
    }
  }

  private load(page: GaPage): void {
    this.loaded = true;
    this.gtagGranted = true;
    const w = window as any;
    this.zone.runOutsideAngular(() => {
      w.dataLayer = w.dataLayer || [];
      // gtag.js reads the arguments object itself, not an array.
      w.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer.push(arguments);
      };
      w.gtag('consent', 'default', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      w.gtag('js', new Date());
      w.gtag('set', page);
      w.gtag('config', gaMeasurementId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
      this.document.head.appendChild(script);
    });
  }

  private disable(): void {
    (window as any)[`ga-disable-${gaMeasurementId}`] = true;
    if (this.loaded && this.gtagGranted) {
      this.gtag('consent', 'update', { analytics_storage: 'denied' });
      this.gtagGranted = false;
    }
    this.deleteGaCookies();
  }

  private sendPageView(): void {
    if (this.consent !== 'granted') {
      return;
    }
    const w = window as any;
    const path = this.routePattern();
    if (path === '/admin' || path.startsWith('/admin/')) {
      w[`ga-disable-${gaMeasurementId}`] = true;
      return;
    }
    w[`ga-disable-${gaMeasurementId}`] = false;
    const location = this.document.location.origin + path;
    const page: GaPage = {
      page_location: location,
      page_referrer: this.previousLocation ?? this.externalReferrer(),
      page_title: this.document.title,
    };
    this.previousLocation = location;
    if (!this.loaded) {
      this.load(page);
    } else if (!this.gtagGranted) {
      this.gtag('consent', 'update', { analytics_storage: 'granted' });
      this.gtagGranted = true;
    }
    // `set`, not the event's own parameters: GA's automatic events (session start, engagement) then
    // carry the same cleaned page instead of the browser's address.
    this.gtag('set', page);
    this.gtag('event', 'page_view');
  }

  // The matched routes' own paths, joined: '/render/preview/:modelId' for /render/preview/abc123?x=1.
  private routePattern(): string {
    const parts: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) {
        parts.push(path);
      }
      route = route.firstChild;
    }
    return '/' + parts.join('/');
  }

  // Where the visitor came from, as an origin only (a Stripe Checkout address carries its session).
  private externalReferrer(): string {
    try {
      const referrer = new URL(this.document.referrer);
      return referrer.origin === this.document.location.origin ? '' : `${referrer.origin}/`;
    } catch {
      return '';
    }
  }

  private gtag(...args: unknown[]): void {
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      this.zone.runOutsideAngular(() => gtag(...args));
    }
  }

  // _ga and _ga_<id>, on every domain GA may have set them for (the host and its parents).
  private deleteGaCookies(): void {
    const names = this.document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'));
    const host = this.document.location.hostname;
    const labels = host.split('.');
    const domains = [''];
    for (let i = 0; i < labels.length - 1; i++) {
      domains.push(`; domain=.${labels.slice(i).join('.')}`);
    }
    for (const name of names) {
      for (const domain of domains) {
        this.document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
      }
    }
  }

  private globalPrivacyControl(): boolean {
    return (navigator as any).globalPrivacyControl === true;
  }

  private readConsent(): AnalyticsConsent | null {
    try {
      const stored = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null') as StoredConsent | null;
      if (!stored || (stored.analytics !== 'granted' && stored.analytics !== 'denied')) {
        return null;
      }
      const at = Date.parse(stored.at);
      return Number.isFinite(at) && Date.now() - at < CONSENT_MAX_AGE_MS ? stored.analytics : null;
    } catch {
      return null;
    }
  }

  private writeConsent(consent: AnalyticsConsent): void {
    try {
      const stored: StoredConsent = { analytics: consent, at: new Date().toISOString() };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
    } catch {
      // Storage off (a private window): the answer holds for this page only.
    }
  }
}
